// client/src/pages/SellerProfilePage.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import ProductCard from '../components/ProductCard';
import {
  Container, Grid, Typography, Rating, Stack, Box, Card, CardContent, TextField, Button,
  Pagination, Alert, CircularProgress, Breadcrumbs, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { getImageUrl, API_BASE_URL } from '../utils/media';

const ReviewForm = ({ sellerId, token, onReviewSubmit }) => {
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/sellers/${sellerId}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rating, review_text: reviewText }),
      });
      if (!response.ok) throw new Error('Failed to submit review.');
      setReviewText('');
      setRating(5);
      onReviewSubmit();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card component="form" onSubmit={handleSubmit} variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>Leave a Review</Typography>
      <Rating value={rating} onChange={(e, val) => setRating(val)} sx={{ mb: 1 }} />
      <TextField fullWidth multiline minRows={2} value={reviewText} onChange={(e) => setReviewText(e.target.value)} required sx={{ mb: 2 }} placeholder="Share your experience with this seller..." />
      <Button type="submit" variant="contained" disabled={submitting}>Submit Review</Button>
    </Card>
  );
};

const SellerProfilePage = () => {
  const { storeName } = useParams();
  const [sellerData, setSellerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ subject: '', message: '' });
  const [contactSent, setContactSent] = useState(false);
  const { user, token } = useContext(AuthContext);

  const fetchSellerData = useCallback(async (page = 1) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sellers/${storeName}?page=${page}`);
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Seller not found.');
      }
      setSellerData(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [storeName]);

  useEffect(() => { fetchSellerData(currentPage); }, [fetchSellerData, currentPage]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Container sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;
  if (!sellerData) return <Container sx={{ py: 4 }}><Typography>Could not load seller data.</Typography></Container>;

  const { seller, products, reviews, stats, pagination } = sellerData;
  const isOwnStore = user && user.userId === seller.user_id;

  const handleSendContact = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sellerId: seller.user_id, subject: contactForm.subject, message: contactForm.message }),
      });
      if (!response.ok) throw new Error('Failed to send message.');
      setContactOpen(false);
      setContactSent(true);
      setContactForm({ subject: '', message: '' });
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 2, overflowX: 'auto', whiteSpace: 'nowrap', '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' } }}
      >
        <Link to="/" style={{ color: 'inherit', textDecoration: 'none' }}>Home</Link>
        <Typography color="text.primary">{seller.store_name}</Typography>
      </Breadcrumbs>

      {seller.banner_url && (
        <Box component="img" src={getImageUrl(seller.banner_url)} alt={`${seller.store_name} banner`}
          sx={{ width: '100%', height: { xs: 140, md: 220 }, objectFit: 'cover', borderRadius: 2, mb: 3 }} />
      )}

      <Box sx={{ mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
          <Typography variant="h4" fontWeight={700} gutterBottom>{seller.store_name}</Typography>
          {!isOwnStore && user && (
            <Button variant="outlined" onClick={() => setContactOpen(true)}>Contact Seller</Button>
          )}
        </Stack>
        {seller.about && <Typography color="text.secondary" sx={{ mb: 1.5, maxWidth: 640 }}>{seller.about}</Typography>}
        {contactSent && <Alert severity="success" sx={{ mb: 2, maxWidth: 480 }}>Message sent! Check "Messages" in your account for the seller's reply.</Alert>}
        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
          {stats && stats.average_rating && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Rating value={Number(stats.average_rating)} precision={0.5} readOnly size="small" />
              <Typography variant="body2" color="text.secondary">({stats.review_count} reviews)</Typography>
            </Stack>
          )}
          <Typography variant="body2" color="text.secondary">
            Member since: {new Date(seller.member_since).toLocaleDateString()}
          </Typography>
        </Stack>
      </Box>

      <Dialog open={contactOpen} onClose={() => setContactOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Contact {seller.store_name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Subject" fullWidth value={contactForm.subject} onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })} />
            <TextField label="Message" fullWidth multiline minRows={3} value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSendContact} disabled={!contactForm.subject || !contactForm.message}>Send</Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Typography variant="h6" fontWeight={700} gutterBottom>Products from this seller</Typography>
          {products.length > 0 ? (
            <Grid container spacing={2}>
              {products.map((product) => (
                <Grid item xs={12} sm={6} key={product.id}>
                  <ProductCard product={product} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography color="text.secondary">This seller has not listed any products yet.</Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography variant="h6" fontWeight={700} gutterBottom>Customer Reviews</Typography>
          {user && user.userId !== seller.user_id && (
            <ReviewForm sellerId={seller.user_id} token={token} onReviewSubmit={() => fetchSellerData(currentPage)} />
          )}
          {reviews.length > 0 ? (
            reviews.map((review) => (
              <Card key={review.id} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Rating value={review.rating} readOnly size="small" />
                  <Typography sx={{ my: 1 }}>{review.review_text}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {review.reviewer_email.split('@')[0]} · {new Date(review.created_at).toLocaleDateString()}
                  </Typography>
                </CardContent>
              </Card>
            ))
          ) : (
            <Typography color="text.secondary">This seller has no reviews yet.</Typography>
          )}
          {pagination && pagination.totalPages > 1 && (
            <Pagination
              count={pagination.totalPages}
              page={pagination.currentPage}
              onChange={(e, page) => setCurrentPage(page)}
              sx={{ mt: 2 }}
            />
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default SellerProfilePage;
